import { Delete, Edit } from '@mui/icons-material';
import { Grid2, IconButton, Typography } from '@mui/material';
import { formatCurrency } from '@utriper/core';
import { DateFormattedUtils, PreviewCost } from '@utriper/core-ui';
import { CostService } from 'core/api/services/cost';
import { PreviewService } from 'core/api/services/preview';
import React, { useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
const CostContext = React.createContext(null);

export const CostProvider = ({ children }) => {
    const { t: translate, i18n } = useTranslation();
    const [costs, setCosts] = useState([]);
    const [costCounts, setCostCounts] = useState(null);
    const [total, setTotal] = useState(0);
    const [cost, setCost] = useState(null);
    const [loading, setLoading] = useState(false);
    const [loadingSubmit, setLoadingSubmit] = useState(false);

    const getCosts = async (options) => {
        try {
            setLoading(true);
            const result = await CostService.getList(options);

            if (!result) {
                throw new Error();
            }
            setCosts(result.costs);
            setCostCounts(result.costCounts);
            setTotal(result.total);
            return true;
        } catch (error) {
            console.error(error);
            setCosts([]);
            setCostCounts(null);
            setTotal(0);
            return false;
        } finally {
            setLoading(false);
        }
    };

    const getCost = async (costId) => {
        if (costId && costId === cost?.id) return true;
        try {
            setLoading(true);
            const result = await CostService.get(costId);

            if (!result) {
                throw new Error();
            }

            setCost(result);
            return true;
        } catch (error) {
            console.error(error);
            setCost(null);
            return false;
        } finally {
            setLoading(false);
        }
    };

    const postCost = async (values) => {
        try {
            setLoadingSubmit(true);
            const result = await CostService.postCost(values);

            if (!result) {
                throw new Error();
            }

            return result;
        } catch (error) {
            console.error(error);
            return false;
        } finally {
            setLoadingSubmit(false);
        }
    };

    const patchCost = async (costId, values) => {
        try {
            setLoadingSubmit(true);
            const result = await CostService.updateCost(costId, values);

            if (!result) {
                throw new Error();
            }

            return true;
        } catch (error) {
            console.error(error);
            return false;
        } finally {
            setLoadingSubmit(false);
        }
    };

    // Utils

    const renderDate = (date, lang = 'es') =>
        DateFormattedUtils(date, lang, {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
            hour: 'numeric',
            minute: 'numeric',
        });

    const costColumns = (showOrigin = false) => {
        const columns = [
            { val: 'id', label: 'Id' },
            { val: 'costId', label: translate('costs.columns.cost_id') },
            { val: 'value', label: translate('costs.columns.value') },
            showOrigin && { val: 'originId', label: translate('costs.columns.originId') },
            { val: 'created', label: translate('costs.columns.created'), style: { minWidth: '100px' } },
            { val: 'updated', label: translate('costs.columns.updated'), style: { minWidth: '100px' } },
            { align: 'left', val: 'actions', label: ' ', style: { minWidth: '84px' } },
        ];
        return columns.filter(Boolean);
    };

    const prettifyCosts = (costs = [], classes, onClickEdit, onClickDelete) =>
        costs?.map((cost) => ({
            ...cost,
            costId: <PreviewCost id={cost?.costId} label="name" previewService={PreviewService} />,
            value: <Typography>{cost?.percentage ? `${cost.percentage}%` : cost?.price ? formatCurrency(cost?.price, cost?.currency, i18n.language) : 'Auto'}</Typography>,
            created: (
                <Grid2 container flexDirection="column" spacing={1} justifyContent="center" alignItems="flex-start">
                    <Typography fontWeight="bold">{renderDate(cost?.createdOn)}</Typography>
                    <Typography fontSize=".8em">{`${translate('costs.created_by')} ${cost?.createdBy}`}</Typography>
                </Grid2>
            ),
            updated: cost?.updatedOn ? (
                <Grid2 container flexDirection="column" spacing={1} justifyContent="center" alignItems="flex-start">
                    <Typography fontWeight="bold">{renderDate(cost?.updatedOn)}</Typography>
                    <Typography fontSize=".8em">{`${translate('costs.updated_by')} ${cost?.updatedBy}`}</Typography>
                </Grid2>
            ) : (
                ''
            ),
            actions: (
                <Grid2 container justifyContent="center" alignItems="center" spacing={1} maxWidth="fit-content">
                    <IconButton
                        className={classes.tableButton}
                        onClick={() => {
                            onClickEdit(cost?.id, cost);
                        }}>
                        <Edit />
                    </IconButton>
                    <IconButton
                        className={classes.tableButton}
                        onClick={() => {
                            onClickDelete(cost?.id);
                        }}>
                        <Delete />
                    </IconButton>
                </Grid2>
            ),
        }));

    return (
        <CostContext.Provider
            value={{
                loading,
                loadingSubmit,
                costs,
                costCounts,
                total,
                cost,
                costColumns,
                getCosts,
                getCost,
                postCost,
                patchCost,
                setCost,
                prettifyCosts,
            }}>
            {children}
        </CostContext.Provider>
    );
};

CostProvider.displayName = 'CostProvider';

export const useCosts = () => useContext(CostContext);
